import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { 
    ArrowLeft, Activity, AlertTriangle, Bone, TrendingUp, Loader2,
    Play, Pause, SkipForward, SkipBack, Maximize2, Zap, Timer,
    Footprints, Target, Eye, Brain, Shield, ChevronRight
} from 'lucide-react';
import { biomechanicsApi, playerTrackingApi, videoAnalysisApi } from '../lib/api';
import { toast } from 'sonner';

// Skeleton connections (pair indices)
const SKELETON_CONNECTIONS = [
    [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],  // Torso + Arms
    [11, 23], [12, 24], [23, 24],                        // Torso to hips
    [23, 25], [25, 27], [24, 26], [26, 28],              // Legs
    [27, 29], [28, 30], [29, 31], [30, 32],              // Feet
    [15, 17], [15, 19], [16, 18], [16, 20],              // Hands
];

const SkeletonOverlay = ({ keypoints, width, height }) => {
    if (!keypoints || keypoints.length === 0) return null;
    
    return (
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-20" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
            {/* Connections */}
            {SKELETON_CONNECTIONS.map(([i, j], idx) => {
                const p1 = keypoints[i];
                const p2 = keypoints[j];
                if (!p1 || !p2 || p1.visibility < 0.5 || p2.visibility < 0.5) return null;
                return (
                    <line key={`conn-${idx}`}
                        x1={p1.x * width} y1={p1.y * height}
                        x2={p2.x * width} y2={p2.y * height}
                        stroke="#22c55e" strokeWidth="3" opacity="0.95"
                    />
                );
            })}
            {/* Keypoints */}
            {keypoints.map((kp, idx) => {
                if (!kp || kp.visibility < 0.5) return null;
                const isJoint = [11,12,13,14,15,16,23,24,25,26,27,28].includes(idx);
                return (
                    <circle key={`kp-${idx}`}
                        cx={kp.x * width} cy={kp.y * height}
                        r={isJoint ? 6 : 3}
                        fill={isJoint ? "#3b82f6" : "#06b6d4"}
                        stroke="white" strokeWidth="1.5"
                    />
                );
            })}
        </svg>
    );
};

const HeatmapOverlay = ({ heatmapData, width, height }) => {
    if (!heatmapData || !heatmapData.grid || heatmapData.grid.length === 0) return null;
    
    const grid = heatmapData.grid;
    const rows = grid.length;
    const cols = grid[0]?.length || 0;
    const cellW = width / cols;
    const cellH = height / rows;
    
    return (
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${width} ${height}`}>
            {grid.map((row, gy) => 
                row.map((value, gx) => {
                    if (value < 0.05) return null;
                    const alpha = Math.min(0.7, value * 0.8);
                    const hue = Math.max(0, 120 - value * 120); // Green to Red
                    return (
                        <rect key={`${gy}-${gx}`}
                            x={gx * cellW} y={gy * cellH}
                            width={cellW} height={cellH}
                            fill={`hsla(${hue}, 100%, 50%, ${alpha})`}
                            rx="2"
                        />
                    );
                })
            )}
        </svg>
    );
};

const BiomechanicsPage = () => {
    const { videoId } = useParams();
    const [analysis, setAnalysis] = useState(null);
    const [bioResult, setBioResult] = useState(null);
    const [trackingResult, setTrackingResult] = useState(null);
    const [keypoints, setKeypoints] = useState(null);
    const [heatmapData, setHeatmapData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [activeView, setActiveView] = useState('skeleton');
    const [currentFrame, setCurrentFrame] = useState(0);
    const [autoPlay, setAutoPlay] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        loadData();
    }, [videoId]);

    // Auto-play frames when enabled
    useEffect(() => {
        if (!autoPlay || !keypoints?.keypoints?.length) return;
        const interval = setInterval(() => {
            setCurrentFrame((prev) => (prev + 1) % keypoints.keypoints.length);
        }, 200);
        return () => clearInterval(interval);
    }, [autoPlay, keypoints]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // Get video analysis
            const videoRes = await videoAnalysisApi.getResult(videoId);
            setAnalysis(videoRes.data);
            
            // Check for existing biomechanics
            const bioRes = await biomechanicsApi.getForVideo(videoId);
            if (bioRes.data.exists) {
                setBioResult(bioRes.data);
                // Load keypoints for skeleton
                if (bioRes.data.id) {
                    try {
                        const kpRes = await biomechanicsApi.getKeypoints(bioRes.data.id, 0, 50);
                        setKeypoints(kpRes.data);
                    } catch (e) {}
                }
            }
            
            // Check for existing tracking
            const trackRes = await playerTrackingApi.getForVideo(videoId);
            if (trackRes.data.exists) {
                setTrackingResult(trackRes.data);
                // Load heatmap
                if (trackRes.data.id) {
                    try {
                        const hmRes = await playerTrackingApi.getHeatmap(trackRes.data.id, 0, 20);
                        setHeatmapData(hmRes.data);
                    } catch (e) {}
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const startBiomechanics = async () => {
        setProcessing(true);
        try {
            const res = await biomechanicsApi.analyze({ video_id: videoId, sport: analysis?.sport, sample_fps: 5, max_frames: 50 });
            toast.success('Biomechanics analysis started! This takes 1-5 minutes.');
            
            // Poll for completion
            const pollInterval = setInterval(async () => {
                try {
                    const result = await biomechanicsApi.getResult(res.data.analysis_id);
                    if (result.data.status === 'completed') {
                        clearInterval(pollInterval);
                        setBioResult(result.data);
                        setProcessing(false);
                        toast.success('Biomechanics analysis complete!');
                        // Load keypoints
                        const kpRes = await biomechanicsApi.getKeypoints(res.data.analysis_id, 0, 50);
                        setKeypoints(kpRes.data);
                    } else if (result.data.status === 'failed') {
                        clearInterval(pollInterval);
                        setProcessing(false);
                        toast.error(`Analysis failed: ${result.data.error}`);
                    }
                } catch (e) {}
            }, 5000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start analysis');
            setProcessing(false);
        }
    };

    const startTracking = async () => {
        setProcessing(true);
        try {
            const res = await playerTrackingApi.analyze({ video_id: videoId, sport: analysis?.sport, sample_fps: 5, max_frames: 100 });
            toast.success('Player tracking started! This takes 2-10 minutes.');
            
            const pollInterval = setInterval(async () => {
                try {
                    const result = await playerTrackingApi.getResult(res.data.tracking_id);
                    if (result.data.status === 'completed') {
                        clearInterval(pollInterval);
                        setTrackingResult(result.data);
                        setProcessing(false);
                        toast.success('Player tracking complete!');
                        const hmRes = await playerTrackingApi.getHeatmap(res.data.tracking_id, 0, 20);
                        setHeatmapData(hmRes.data);
                    } else if (result.data.status === 'failed') {
                        clearInterval(pollInterval);
                        setProcessing(false);
                        toast.error(`Tracking failed: ${result.data.error}`);
                    }
                } catch (e) {}
            }, 5000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start tracking');
            setProcessing(false);
        }
    };

    const getCurrentKeypoints = () => {
        if (!keypoints?.keypoints || currentFrame >= keypoints.keypoints.length) return null;
        return keypoints.keypoints[currentFrame];
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] text-white flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 md:px-6">
                    <div className="flex items-center justify-between h-14 md:h-16">
                        <div className="flex items-center gap-2">
                            <Link to={`/video-analysis/${videoId}`} className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-5 h-5" />
                            </Link>
                            <Bone className="w-5 h-5 text-primary" />
                            <span className="font-bold">Body Tracking & Analytics</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{analysis?.athlete_name}</span>
                            <span className="text-primary">| {analysis?.sport}</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
                {/* View Toggle */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                    {[
                        { id: 'skeleton', icon: Bone, label: 'Skeleton Overlay' },
                        { id: 'heatmap', icon: Target, label: 'Movement Heatmap' },
                        { id: 'metrics', icon: Activity, label: 'Biomechanics Metrics' },
                        { id: 'injuries', icon: Shield, label: 'Injury Risk' },
                    ].map(tab => (
                        <Button key={tab.id} size="sm"
                            variant={activeView === tab.id ? 'default' : 'outline'}
                            onClick={() => setActiveView(tab.id)}
                            className={activeView !== tab.id ? 'border-white/10' : ''}
                            data-testid={`view-${tab.id}`}
                        >
                            <tab.icon className="w-4 h-4 mr-1" />{tab.label}
                        </Button>
                    ))}
                </div>

                {/* Video + Overlay */}
                <div className="grid gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                        <Card className="bg-[#0d2847] border-white/5">
                            <CardContent className="p-4">
                                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                                    {!videoError ? (
                                        <video ref={videoRef}
                                            src={`${process.env.REACT_APP_BACKEND_URL}/api/video-analysis/video/${videoId}`}
                                            className="w-full h-full object-contain"
                                            controls
                                            onError={() => setVideoError(true)}
                                            data-testid="bio-video-player"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#0a1628] via-[#0d2847] to-[#0a1628]">
                                            <div className="text-center text-white/40 z-0">
                                                <Bone className="w-16 h-16 mx-auto mb-3 opacity-30" />
                                                <p className="text-sm font-medium">Demo Mode — Skeleton Animation</p>
                                                <p className="text-xs mt-1 opacity-70">No source video; rendering keypoint overlay only</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Skeleton Overlay */}
                                    {activeView === 'skeleton' && getCurrentKeypoints() && (
                                        <SkeletonOverlay keypoints={getCurrentKeypoints()} width={640} height={360} />
                                    )}
                                    
                                    {/* Heatmap Overlay */}
                                    {activeView === 'heatmap' && heatmapData && (
                                        <HeatmapOverlay heatmapData={heatmapData.heatmap} width={640} height={360} />
                                    )}
                                </div>
                                
                                {/* Frame scrubber for skeleton */}
                                {activeView === 'skeleton' && keypoints && (
                                    <div className="mt-3 space-y-2">
                                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                                            <span>Frame: {currentFrame + 1}/{keypoints.total_frames}</span>
                                            <span>{keypoints.timestamps?.[currentFrame]?.toFixed(2)}s</span>
                                        </div>
                                        <input type="range" min="0" max={Math.min(keypoints.keypoints?.length - 1, 49)}
                                            value={currentFrame} onChange={(e) => setCurrentFrame(parseInt(e.target.value))}
                                            className="w-full h-2 rounded-lg appearance-none bg-[#0a1628] cursor-pointer"
                                            data-testid="frame-scrubber"
                                        />
                                        <div className="flex gap-2 justify-center">
                                            <Button size="sm" variant="ghost" onClick={() => setCurrentFrame(Math.max(0, currentFrame - 1))} data-testid="frame-prev-btn">
                                                <SkipBack className="w-4 h-4" />
                                            </Button>
                                            <Button size="sm" variant={autoPlay ? "default" : "ghost"} onClick={() => setAutoPlay(!autoPlay)} data-testid="frame-autoplay-btn">
                                                {autoPlay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                                <span className="ml-1 text-xs">{autoPlay ? 'Pause' : 'Auto-Play'}</span>
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => setCurrentFrame(Math.min((keypoints.keypoints?.length || 1) - 1, currentFrame + 1))} data-testid="frame-next-btn">
                                                <SkipForward className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                {!bioResult?.status && !trackingResult?.status && (
                                    <div className="mt-4 flex flex-wrap gap-3">
                                        <Button onClick={startBiomechanics} disabled={processing} data-testid="start-biomechanics-btn">
                                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bone className="w-4 h-4 mr-2" />}
                                            Analyze Biomechanics (MediaPipe)
                                        </Button>
                                        <Button onClick={startTracking} disabled={processing} variant="outline" className="border-white/10" data-testid="start-tracking-btn">
                                            {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Footprints className="w-4 h-4 mr-2" />}
                                            Track Players (YOLOv8)
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Panel */}
                    <div className="space-y-4">
                        {/* Biomechanics Metrics */}
                        {activeView === 'metrics' && bioResult?.sport_metrics && (
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-sm">
                                        <Activity className="w-4 h-4 text-primary" />Biomechanics Metrics
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {Object.entries(bioResult.sport_metrics).map(([key, value]) => (
                                        <div key={key} className="space-y-1">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                                                <span className="font-medium">{typeof value === 'number' ? value.toFixed(1) : value}</span>
                                            </div>
                                            {typeof value === 'number' && value <= 100 && (
                                                <div className="h-1.5 bg-[#0a1628] rounded-full overflow-hidden">
                                                    <div className={`h-full rounded-full ${value >= 80 ? 'bg-green-500' : value >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                        style={{ width: `${Math.min(100, value)}%` }} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                        {/* Injury Risk */}
                        {activeView === 'injuries' && (
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-sm">
                                        <Shield className="w-4 h-4 text-red-400" />Injury Risk Assessment
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {bioResult?.injury_risks?.length > 0 ? (
                                        <div className="space-y-3">
                                            {bioResult.injury_risks.map((risk, i) => (
                                                <div key={i} className={`p-3 rounded-lg border ${
                                                    risk.severity === 'high' ? 'border-red-500/30 bg-red-500/5' : 'border-yellow-500/30 bg-yellow-500/5'
                                                }`}>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <AlertTriangle className={`w-4 h-4 ${risk.severity === 'high' ? 'text-red-400' : 'text-yellow-400'}`} />
                                                        <span className="text-sm font-medium capitalize">{risk.type.replace(/_/g, ' ')}</span>
                                                        <span className={`text-xs px-2 py-0.5 rounded ${risk.severity === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                                            {risk.severity}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">{risk.description}</p>
                                                    <p className="text-xs text-primary mt-1">{risk.recommendation}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-6">
                                            {bioResult?.status === 'completed' ? (
                                                <>
                                                    <Shield className="w-8 h-8 mx-auto text-green-400 mb-2" />
                                                    <p className="text-sm text-green-400">No significant injury risks detected</p>
                                                    <p className="text-xs text-muted-foreground">Movement patterns appear healthy</p>
                                                </>
                                            ) : (
                                                <>
                                                    <Shield className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                                                    <p className="text-sm text-muted-foreground">Run biomechanics analysis to assess injury risk</p>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Player Tracking Stats */}
                        {activeView === 'heatmap' && trackingResult && (
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-sm">
                                        <Footprints className="w-4 h-4 text-primary" />Movement Stats
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {trackingResult.player_analytics && Object.values(trackingResult.player_analytics).map((player, i) => (
                                        <div key={i} className="space-y-2 mb-4 last:mb-0">
                                            <p className="text-xs font-medium text-primary">Player {player.player_id + 1}</p>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div className="p-2 bg-[#0a1628] rounded text-center">
                                                    <p className="text-lg font-bold">{player.total_distance_m}m</p>
                                                    <p className="text-xs text-muted-foreground">Distance</p>
                                                </div>
                                                <div className="p-2 bg-[#0a1628] rounded text-center">
                                                    <p className="text-lg font-bold">{player.max_speed_kmh} km/h</p>
                                                    <p className="text-xs text-muted-foreground">Top Speed</p>
                                                </div>
                                                <div className="p-2 bg-[#0a1628] rounded text-center">
                                                    <p className="text-lg font-bold">{player.sprint_count}</p>
                                                    <p className="text-xs text-muted-foreground">Sprints</p>
                                                </div>
                                                <div className="p-2 bg-[#0a1628] rounded text-center">
                                                    <p className="text-lg font-bold">{player.avg_speed_ms} m/s</p>
                                                    <p className="text-xs text-muted-foreground">Avg Speed</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {!trackingResult.player_analytics && (
                                        <p className="text-center text-sm text-muted-foreground py-4">No tracking data available</p>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Skeleton info */}
                        {activeView === 'skeleton' && (
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2 text-sm">
                                        <Bone className="w-4 h-4 text-primary" />Skeleton Overlay
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {bioResult?.status === 'completed' ? (
                                        <div className="space-y-2">
                                            <p className="text-xs text-muted-foreground">
                                                33 keypoints tracked across {bioResult.total_keypoint_frames || bioResult.processed_frames || bioResult.frame_count || 0} frames
                                            </p>
                                            <div className="flex items-center gap-3 text-xs">
                                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block"></span> Joints</span>
                                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-cyan-400 inline-block"></span> Points</span>
                                                <span className="flex items-center gap-1"><span className="w-3 h-1 bg-green-500 inline-block rounded"></span> Bones</span>
                                            </div>
                                            {bioResult.overall_score && (
                                                <div className="mt-3 p-3 bg-[#0a1628] rounded-lg text-center">
                                                    <p className="text-3xl font-bold text-primary">{bioResult.overall_score}</p>
                                                    <p className="text-xs text-muted-foreground">Biomechanics Score</p>
                                                </div>
                                            )}
                                        </div>
                                    ) : bioResult?.status === 'processing' ? (
                                        <div className="text-center py-4">
                                            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                                            <p className="text-xs text-muted-foreground">Processing frames...</p>
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <Bone className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                                            <p className="text-xs text-muted-foreground">Click "Analyze Biomechanics" to start</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Analysis Status */}
                        <Card className="bg-[#0d2847] border-white/5">
                            <CardContent className="p-4 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground">Biomechanics</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                        bioResult?.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                        bioResult?.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                                        'bg-gray-500/20 text-gray-400'
                                    }`}>{bioResult?.status || 'Not started'}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground">Player Tracking</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                        trackingResult?.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                        trackingResult?.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                                        'bg-gray-500/20 text-gray-400'
                                    }`}>{trackingResult?.status || 'Not started'}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default BiomechanicsPage;
